import React from 'react';
import { IChartData, E_CHART_GROUP_IDENTIFIERS, E_DOMAIN_Y_SCALE } from './chartInterfaces';
import ChartScoresService from '../../services/ChartScoresService';
import { inject, observer } from 'mobx-react';
import { IMobxRootState } from '../../RootStore';
import Chart from './Chart';
import { Moment } from 'moment';
import chartStore, { COMPOSITE_DATA_INTERVAL } from './ChartStore';
import EventsService from '../../services/EventsService';
import CancelableEvents from '../../utils/CancelableEvents';
import { VIEW, GLOBAL_INDEXES, navbarContingentGroup } from '../../interfaces';
import { toJS } from 'mobx';
import APIService, {
    E_RISK_TYPE,
    IAPIMaxImpactMap,
    RISK_CHART_MODES,
} from '../../services/APIService';
import loading from '../../services/LoadingService';
import {
    first,
    isEmpty,
    map,
    filter,
    flow,
    forEach,
    isEqual,
    includes,
    get,
} from 'lodash/fp';
import ChartData from './ChartData';
import { C_NO_PERMISSIONS_RISK } from '../../constants';
import { E_TOAST_TYPE } from 'components/GQToast/GQToast';
import { GQToastContainer } from 'components/GQToast/GQToastContainer';
import ChartToolbar from './ChartToolbar';
import rootStore from '../../RootStore';
import { getRiskLongName } from 'utils/riskUtils';
import { translateGlobalIndex } from 'utils/renderUtils';
import { TimelineButtons } from './TimelineButtons';
import { SliderHorizontalProvider } from './SliderHorizontal';
import { FGStack, FGTypography } from '@fg/design-system';

type State = {
    data: IChartData[];
};

type Props = {
    onPan?: (pan: number) => void;
    onVirualTodayChange?: (date: Moment) => void;
    onWheel?: (isUp: boolean) => void;
    hasToolbar: boolean;
    showVirtualToday: boolean;
    activeView: VIEW;
    minX?: Moment;
    maxX?: Moment;
    width?: number;
    height?: number;
    virtualToday?: Moment;
    showLines?: boolean;
    isDragging?: boolean;
    currencyCountry?: number[];
    macroeconomicsCountry?: number[];
    activeMacroeconomicsIndicator?: string;
    countries?: number[];
    identifiers?: number[];
    identifierType?: E_RISK_TYPE;
    isDimensionsMultiSelect?: boolean;
    showExchange?: boolean;
    showProjectionData?: boolean;
    showRiskMarkersData?: boolean;
    showPulseData?: boolean;
    showGeoquantLine?: boolean;
    sequence?: number;
    annoMode?: boolean;
    hasActiveCustomWeight?: boolean;
    updateChartDimensions?: () => void;
    activeGlobalIndicators?: GLOBAL_INDEXES[];
    changeDataWhenDragStop?: boolean;
    showSlider?: boolean;
    showTooltip?: boolean;
    riskChartMode?: RISK_CHART_MODES;
    compositeDataInterval?: COMPOSITE_DATA_INTERVAL;
    isExportComponent?: boolean;
    preloadedData?: IChartData[];
    disableAutoLoad?: boolean;
};

const RISK_ENTITY_CHART_LINES = [
    E_CHART_GROUP_IDENTIFIERS.POLITICAL_RISK,
    E_CHART_GROUP_IDENTIFIERS.CONTINGENT_RISK,
    E_CHART_GROUP_IDENTIFIERS.PULSE,
];

@inject(
    (
        {
            chartStore,
            risksStore,
            countryStore,
            customWeightStore,
            insightsStore,
        }: IMobxRootState,
        nextProps: Props
    ): Partial<Props> => {
        // todo:eventually ChartDataProvider should receive the countries list as a prop depending on each page's country list data structure
        // (eg - Contingent page will pass the first country + secondary countries list as a single array, while the map page will pass the selected country list array etc.)
        // ChartDataProvider should not decide or know how to obtain its country list since this is a concern of each page in the app.
        // the current plan is to:
        // 1. make the ChartDataProvider decide which country store to use
        // 2. convert HomeController mega-component into multiple components - each component per app page (eg: ContingentPage, WorldGrpahPage, CountryPage etc.)
        // 3. Each page will have (or not have) its own map component which will receive the countries list as a prop from the page component
        return {
            minX: chartStore.baseDate
                .clone()
                .startOf('day')
                .subtract(chartStore.rangeLeft, 'days'),
            maxX: chartStore.baseDate
                .clone()
                .startOf('day')
                .add(chartStore.rangeRight, 'days'),
            width: chartStore.width,
            height: chartStore.height,
            virtualToday: chartStore.virtualToday,
            showLines: chartStore.showLines,
            isDragging: chartStore.isDragging,
            countries: countryStore.currentCountryList,
            identifiers: risksStore.currentList,
            identifierType: risksStore.currentRisksType,
            isDimensionsMultiSelect:
                nextProps.activeView === VIEW.COUNTRY_GRAPH,
            showExchange: risksStore.getIsCurrencyOn(),
            currencyCountry: risksStore.getCurrencyCountry(),
            macroeconomicsCountry: risksStore.macroeconomicsCountry,
            activeMacroeconomicsIndicator: risksStore.activeMacroeconomicsIndicator,
            sequence: chartStore.sequence,
            showGeoquantLine: risksStore.showGeoquantLine,
            showProjectionData:
                !customWeightStore.hasActiveCustomWeight &&
                risksStore.projectionDataOn,
            showPulseData:
                !customWeightStore.hasActiveCustomWeight &&
                risksStore.showPulseData,
            showRiskMarkersData: risksStore.riskMarkersOn,
            annoMode: insightsStore.annotationMode,
            hasActiveCustomWeight: customWeightStore.hasActiveCustomWeight,
            updateChartDimensions: chartStore.updateDimensions,
            showVirtualToday: chartStore.showVirtualToday,
            activeGlobalIndicators: risksStore.activeGlobalIndicators,
            riskChartMode: risksStore.riskChartMode,
            compositeDataInterval: chartStore.compositeDataInterval,
        };
    }
)
@observer
export default class ChartDataProvider extends React.Component<Props, State> {
    private cancelable: CancelableEvents;
    private toaster: GQToastContainer;

    constructor(props: Props) {
        super(props);
        this.state = {
            data: props.preloadedData || [],
        };
    }

    public componentDidMount() {
        this.cancelable = new CancelableEvents();

        if (this.props.preloadedData) {
            this.setState({
                data: this.props.preloadedData,
            });
            //return;
        }

        else if (!this.props.disableAutoLoad) {
            this.loadRisksData();
            this.loadCurrencyExchangeData();
            this.loadGlobalIndexChart();
        }

        this.cancelable.addEventSubscription(
            EventsService.registerOnReloadCountry(this.onReloadCountryCB)
        );

        this.cancelable.setTimeout(() => {
            this.props.updateChartDimensions();
        });
    }

    public componentWillUnmount() {
        this.cancelable.cancelAll();
    }

    public async componentDidUpdate(prevProps: Props) {
        if (this.props.disableAutoLoad) {
            if (
                prevProps.width !== this.props.width ||
                prevProps.height !== this.props.height
            ) {
                this.cancelable.setTimeout(() => {
                    this.props.updateChartDimensions();
                });
            }

            if (!isEqual(prevProps.preloadedData, this.props.preloadedData)) {
                this.setState({
                    data: this.props.preloadedData || [],
                });
            }

            return;
        }

        if (
            prevProps.width !== this.props.width ||
            prevProps.height !== this.props.height
        ) {
            this.cancelable.setTimeout(() => {
                this.props.updateChartDimensions();
            });
        }

        this.reloadRisks(prevProps);

        if (
            !isEqual(this.props.sequence, prevProps.sequence) ||
            this.props.currencyCountry.toString() !==
                prevProps.currencyCountry.toString() ||
            !isEqual(this.props.showExchange, prevProps.showExchange) ||
            !this.props.minX.isSame(prevProps.minX, 'days') ||
            !this.props.maxX.isSame(prevProps.maxX, 'days')
        ) {
            if (!isEmpty(this.props.currencyCountry)) {
                loading.start();
            }
            await this.loadCurrencyExchangeData();
            if (!isEmpty(this.props.currencyCountry)) {
                loading.stop();
            }
        }

        if (
            !isEqual(this.props.activeView, prevProps.activeView) ||
            !isEqual(this.props.countries, prevProps.countries) ||
            !this.props.minX.isSame(prevProps.minX, 'days') ||
            !this.props.maxX.isSame(prevProps.maxX, 'days') ||
            !isEqual(
                toJS(this.props.activeGlobalIndicators),
                toJS(prevProps.activeGlobalIndicators)
            ) ||
            !isEqual(
                toJS(this.props.macroeconomicsCountry),
                toJS(prevProps.macroeconomicsCountry)
            ) ||
            !isEqual(
                this.props.activeMacroeconomicsIndicator,
                prevProps.activeMacroeconomicsIndicator
            )
        ) {
            if (!isEmpty(this.props.activeGlobalIndicators)) {
                loading.start();
            }
            await this.loadGlobalIndexChart();
            if (!isEmpty(this.props.activeGlobalIndicators)) {
                loading.stop();
            }
        }

        if (this.props.activeGlobalIndicators.length === 0) {
            this.removeChartData(E_CHART_GROUP_IDENTIFIERS.GLOBAL_INDEX);
        }
    }

    private getChartTitle (){
        if (this.props.activeView === VIEW.COUNTRY_GRAPH) {
            const selectedCountryId = first(
                rootStore.countryStore.currentCountryList
            );
            return rootStore.countryStore.countries.countries[selectedCountryId]
                ?.name;
        }

        return getRiskLongName(
            rootStore.risksStore.currentRisksType,
            first(rootStore.risksStore.currentList)
        );
    }

    private getIndicatorLabel(): React.ReactNode {
        const { data } = this.state;
        if (!data) return null;
        const economicData = filter(
            (d: IChartData) => includes(d.identifier, [
                E_CHART_GROUP_IDENTIFIERS.CURRENCY_EXCHANGE,
                E_CHART_GROUP_IDENTIFIERS.GLOBAL_INDEX,
            ]),
            data
        );
        if (isEmpty(economicData)) return null;
        const firstLine = economicData[0]?.lines?.[0];
        if (!firstLine) return null;
        const countries = rootStore.countryStore.countries.countries;
        const indicator = firstLine.extraData?.indicator;
        let label;
        if (
            economicData[0].identifier ===
            E_CHART_GROUP_IDENTIFIERS.CURRENCY_EXCHANGE
        ) {
            const country = countries[firstLine.country];
            const code = country?.currency_code;
            const pair = code
                ? code === 'USD'
                    ? 'EUR:USD'
                    : `USD:${code}`
                : '';
            label = pair ? `Currency (${pair})` : 'Currency';
        } else {
            const translated =
                translateGlobalIndex[indicator] || indicator || 'Commodity';
            const country = countries[firstLine.country];
            const countryCode = country?.abbreviation;
            label = countryCode ? `${translated} (${countryCode})` : translated;
        }
        return (
            <FGStack style={{ flex: 1 }} justifyContent="flex-end">
                <FGTypography variant="captions" tag="label" color="strong">
                    <span
                        style={{
                            color: this.getIndicatorColor(),
                            marginRight: 8,
                        }}>
                        ---
                    </span>
                    {label}
                </FGTypography>
            </FGStack>
        );
    }

    private getIndicatorColor(): string | null {
        const { data } = this.state;
        if (!data) return null;
        const economicData = filter(
            (d: IChartData) => includes(d.identifier, [
                E_CHART_GROUP_IDENTIFIERS.CURRENCY_EXCHANGE,
                E_CHART_GROUP_IDENTIFIERS.GLOBAL_INDEX,
            ]),
            data
        );
        if (isEmpty(economicData)) return null;
        return economicData[0]?.lines?.[0]?.props?.stroke as string || null;
    }

    public render() {
        return (
            <>
                <GQToastContainer ref={el => (this.toaster = el)} />
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        width: '100%',
                    }}>
                    {this.props.hasToolbar && <ChartToolbar
                        hideActivePreset={navbarContingentGroup.has(
                            this.props.activeView
                        )}
                        hideMonthlyToggle={
                            this.props.activeView === VIEW.CONTINGENT_GRAPH ||
                            this.props.isExportComponent
                        }
                        title={this.getChartTitle()}
                        isExportComponent={this.props.isExportComponent}
                    />}
                    <Chart
                        isExportComponent={this.props.isExportComponent}
                        activeView={this.props.activeView}
                        data={this.state.data}
                        minX={this.props.minX}
                        maxX={this.props.maxX}
                        width={this.props.width}
                        height={this.props.height}
                        virtualToday={this.props.virtualToday}
                        emitter={null}
                        onVirualTodayChange={this.props.onVirualTodayChange}
                        onWheel={this.props.onWheel}
                        showLines={this.props.showLines}
                        onPan={this.props.onPan}
                        isDragging={this.props.isDragging}
                        onDragChange={this.onDragStateChange}
                        annoMode={this.props.annoMode}
                        hasToolbar={this.props.hasToolbar}
                        showTooltip={this.props.showTooltip}
                        showVirtualToday={this.props.showVirtualToday}
                        changeDataWhenDragStop={
                            this.props.changeDataWhenDragStop
                        }
                        countries={rootStore.countryStore.countries.countries}
                        isDimensionsMultiSelect={
                            this.props.isDimensionsMultiSelect
                        }
                        compositeDataInterval={this.props.compositeDataInterval}
                        showRiskMarkersData={this.props.showRiskMarkersData}
                    />
                    {this.props.showSlider && <SliderHorizontalProvider />}
                    {!this.props.isExportComponent && 
                    <TimelineButtons
                        hidePastProjectionButton={
                            this.props.activeView === VIEW.CONTINGENT_GRAPH
                        }
                        hideRiskMarkerButton={
                            this.props.activeView === VIEW.CONTINGENT_GRAPH
                        }
                        indicatorLabel={this.getIndicatorLabel()}
                    />}
                </div>
            </>
        );
    }

    private reloadRisks(prevProps: Props) {
        if (
            !isEqual(this.props.activeView, prevProps.activeView) ||
            !isEqual(this.props.sequence, prevProps.sequence) ||
            !isEqual(this.props.countries, prevProps.countries) ||
            !isEqual(this.props.identifiers, prevProps.identifiers) ||
            !isEqual(this.props.identifierType, prevProps.identifierType) ||
            !isEqual(
                this.props.isDimensionsMultiSelect,
                prevProps.isDimensionsMultiSelect
            ) ||
            !isEqual(
                this.props.showProjectionData,
                prevProps.showProjectionData
            ) ||
            !isEqual(this.props.showPulseData, prevProps.showPulseData) ||
            !isEqual(this.props.showGeoquantLine, prevProps.showGeoquantLine) ||
            !isEqual(
                this.props.hasActiveCustomWeight,
                prevProps.hasActiveCustomWeight
            ) ||
            !this.props.minX.isSame(prevProps.minX, 'days') ||
            !this.props.maxX.isSame(prevProps.maxX, 'days') ||
            !this.props.virtualToday.isSame(prevProps.virtualToday, 'days') ||
            this.props.compositeDataInterval !== prevProps.compositeDataInterval
        ) {
            this.loadRisksData(prevProps);
        }
    }

    private onDragStateChange = (isDragging: boolean) => {
        chartStore.setState({
            isDragging,
        });
    };

    private getPoliticalRiskQuery() {
        return {
            from: this.props.minX,
            until: this.props.maxX,
            countries: this.props.isDimensionsMultiSelect
                ? [first(this.props.countries)].filter(Boolean)
                : this.props.countries,
            identifiers: this.props.identifiers,
            identifierType: this.props.identifierType,
            isDimensionBased: this.props.isDimensionsMultiSelect,
            projectionDate:
                this.props.showProjectionData
                    ? this.props.virtualToday
                    : null,
            geoquantLine: this.props.showGeoquantLine,
            showPulseData:
                this.props.showPulseData &&
                this.props.compositeDataInterval !==
                    COMPOSITE_DATA_INTERVAL.MONTHLY,
            isMonthly:
                this.props.compositeDataInterval ===
                COMPOSITE_DATA_INTERVAL.MONTHLY,
        };
    }

    private async loadContingentRisks(prevProps?: Props) {
        this.removeChartData(E_CHART_GROUP_IDENTIFIERS.POLITICAL_RISK);
        this.removeChartData(E_CHART_GROUP_IDENTIFIERS.PULSE);
        loading.start();
        let risks;
        try {
            risks = await APIService.getContingentRisk(
            first(this.props.identifiers),
            first(this.props.countries)
        );
        } catch(err) {
            loading.stop();
            if (403 === get(['response', 'status'], err)){
                this.toaster.createToast(C_NO_PERMISSIONS_RISK, {
                    type: E_TOAST_TYPE.ERROR,
                });
            }
            return;
        }
        const linesToRemove = [
            E_CHART_GROUP_IDENTIFIERS.POLITICAL_RISK,
            E_CHART_GROUP_IDENTIFIERS.CONTINGENT_RISK,
            E_CHART_GROUP_IDENTIFIERS.PULSE,
        ];
        this.setState(state => ({
            data: state.data.filter(
                line => !includes(line.identifier, linesToRemove)
            ),
        }));
        const baseCountryId = this.props.countries[0];
        const baseCountryChanged: boolean =
            prevProps &&
            prevProps.countries[0] &&
            prevProps.countries[0] !== baseCountryId;
        const noBaseCountry: boolean =
            !prevProps &&
            (!this.props.countries || this.props.countries.length === 0);
        if (noBaseCountry || baseCountryChanged) {
            this.removeChartData(E_CHART_GROUP_IDENTIFIERS.CONTINGENT_RISK);
        } else {
            const contingentCountriesIds = this.props.countries.slice(1);
            if (!isEmpty(contingentCountriesIds)) {
                const countryMaxImpactMaps: {
                    [countryId: number]: IAPIMaxImpactMap;
                } = {};
                const maxImpactMapPromises = contingentCountriesIds.map(
                    countryId => {
                        return APIService.getMaxImpactMaps(
                            E_RISK_TYPE.CONTINGENT,
                            first(this.props.identifiers),
                            [baseCountryId, countryId]
                        )
                            .then(
                                countryMaxImpactMap =>
                                    (countryMaxImpactMaps[
                                        countryId
                                    ] = countryMaxImpactMap)
                            )
                            .catch(
                                () =>
                                    (countryMaxImpactMaps[
                                        countryId
                                    ] = {} as IAPIMaxImpactMap)
                            );
                    }
                );
                await Promise.all(maxImpactMapPromises);

                const contingentChartLines = flow(
                    filter(
                        (currentCountryId: number) => !!risks[currentCountryId]
                    ),
                    map((currentCountryId: number) => {
                        const targetRiskData = risks[currentCountryId];
                        const targetMaxImpactMap =
                            countryMaxImpactMaps[currentCountryId];
                        const currentCountryLineData = ChartScoresService.generateContingentRiskLine(
                            currentCountryId,
                            targetRiskData,
                            targetMaxImpactMap
                        );

                        return currentCountryLineData;
                    })
                )(contingentCountriesIds);

                const contingentRiskCharts = await ChartScoresService.getContingentRiskCharts(
                    contingentChartLines,
                    this.getPoliticalRiskQuery()
                );

                forEach((chart: ChartData) => {
                    this.setChartData(
                        chart,
                        chart.identifier
                    );
                }, contingentRiskCharts);
            } else {
                this.removeChartData(E_CHART_GROUP_IDENTIFIERS.CONTINGENT_RISK);
            }
        }
        loading.stop();
    }

    private async loadRisksData(prevProps?: Props) {
        if (isEmpty(this.props.identifiers) || isEmpty(this.props.countries)) {
            this.setState({
                data: this.state.data.filter(
                    line => !includes(line.identifier, RISK_ENTITY_CHART_LINES)
                ),
            });
            return;
        }

        if (navbarContingentGroup.has(this.props.activeView)) {
            this.loadContingentRisks(prevProps);
        } else {
            this.loadCompositeRisks();
        }
    }

    private loadCompositeRisks = async () => {
        try {
            const query = this.getPoliticalRiskQuery();
            if (isEmpty(query.countries) || isEmpty(query.identifiers)) {
                return;
            }

            loading.start();
            const charts = await this.cancelable.promise(() => {
                return ChartScoresService.getPoliticalRiskChart(query);
            });
            // Preserve non-risk chart lines (e.g. "Compare with" external data
            // such as currency / commodity / macroeconomics) regardless of
            // daily/monthly mode, so that switching resolutions or moving the
            // timeline does not wipe the external data overlay.
            this.setState(state => ({
                data: [
                    ...charts,
                    ...state.data.filter(line =>
                        !includes(line.identifier, RISK_ENTITY_CHART_LINES)
                    ),
                ],
            }));
        } catch (err) {
            if (CancelableEvents.isCanceledPromiseError(err as Error)) {
                return;
            }
            console.error(err);
            if (403 === get(['response', 'status'], err)){
                this.toaster.createToast(C_NO_PERMISSIONS_RISK, {
                    type: E_TOAST_TYPE.ERROR,
                });
            }
        } finally {
            loading.stop();
        }
    }

    private async loadCurrencyExchangeData() {
        try {
            if (this.props.showExchange) {
                const linesData = await this.cancelable.promise(() => {
                    return ChartScoresService.getCurrencyExchange({
                        countries: this.props.currencyCountry,
                    });
                });
                const chart = new ChartData(
                    linesData,
                    {
                        expanded: E_DOMAIN_Y_SCALE.standalone,
                        minimized: E_DOMAIN_Y_SCALE.standalone,
                    },
                    E_CHART_GROUP_IDENTIFIERS.CURRENCY_EXCHANGE,
                    this.props.minX.clone().startOf('day'),
                    this.props.maxX.clone().endOf('day')
                );
                this.setChartData(
                    chart,
                    E_CHART_GROUP_IDENTIFIERS.CURRENCY_EXCHANGE
                );
            } else {
                this.removeChartData(
                    E_CHART_GROUP_IDENTIFIERS.CURRENCY_EXCHANGE
                );
            }
        } catch (err) {
            if (CancelableEvents.isCanceledPromiseError(err as Error)) {
                return;
            }
            console.error(err);
        }
    }

    private async loadGlobalIndexChart() {
        try {
            const { activeGlobalIndicators, macroeconomicsCountry } = this.props;
            if (activeGlobalIndicators.length === 0) {
                return this.removeChartData(
                    E_CHART_GROUP_IDENTIFIERS.GLOBAL_INDEX
                );
            }

            const chart = await this.cancelable.promise(() => {
                return ChartScoresService.getGlobalIndexChart(
                    activeGlobalIndicators,
                    this.props.minX,
                    this.props.maxX,
                    first(macroeconomicsCountry)
                );
            });
            this.setChartData(chart, E_CHART_GROUP_IDENTIFIERS.GLOBAL_INDEX);
        } catch (err) {
            if (CancelableEvents.isCanceledPromiseError(err as Error)) {
                return;
            }
            console.error(err);
        }
    }

    private setChartData(chart: IChartData, type: E_CHART_GROUP_IDENTIFIERS) {
        this.setState(state => {
            return {
                data: state.data
                    .filter(c => c.identifier !== type)
                    .concat(chart),
            };
        });
    }

    private removeChartData(type: E_CHART_GROUP_IDENTIFIERS) {
        const filteredData = this.state.data.filter(c => c.identifier !== type);
        if (filteredData.length !== this.state.data.length) {
            this.setState({
                data: filteredData,
            });
        }
    }

    private onReloadCountryCB = (cids: number[]) => {
        this.cancelable.setTimeout(() => {
            let shouldSequence = false;
            forEach(cid => {
                if (this.props.countries.includes(cid)) {
                    shouldSequence = true;
                }
            }, cids);
            if (shouldSequence) {
                chartStore.increaseSequence();
            }
        }, 0);
    };
}
